import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bindTypeaheadReposition, computeFixedTypeaheadStyle } from '../../utils/typeaheadPosition';

export type CffSelectOption = {
  value: string;
  label: string;
};

type CffSearchableSelectProps = {
  value: string;
  options: CffSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputClassName?: string;
  ariaLabel?: string;
};

/** Combobox: type to filter, pick only from options (no free-text values). */
export function CffSearchableSelect({
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Search and select…',
  inputClassName = 'field-input',
  ariaLabel,
}: CffSearchableSelectProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(() => selected?.label ?? '');
  const [highlight, setHighlight] = useState(0);
  const [listStyle, setListStyle] = useState<React.CSSProperties>({});
  const editingRef = useRef(false);

  useEffect(() => {
    if (editingRef.current) return;
    setQuery(selected?.label ?? (value ? value : ''));
  }, [selected, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const updateListPosition = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    setListStyle(
      computeFixedTypeaheadStyle(rect, {
        minWidth: Math.max(rect.width, 280),
        maxWidth: 520,
        maxHeightCap: 280,
        gap: 2,
        zIndex: 4200,
      }),
    );
  };

  useEffect(() => {
    if (!open) return;
    updateListPosition();
    return bindTypeaheadReposition(updateListPosition);
  }, [open, query, filtered.length]);

  const pick = (opt: CffSelectOption) => {
    editingRef.current = false;
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
  };

  const revertDisplay = () => {
    editingRef.current = false;
    setQuery(selected?.label ?? (value ? value : ''));
  };

  return (
    <div ref={rootRef} className="dem-typeahead cff-async-typeahead">
      <input
        ref={inputRef}
        type="text"
        className={inputClassName}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        role="combobox"
        autoComplete="off"
        onFocus={() => {
          editingRef.current = true;
          setOpen(true);
          setHighlight(0);
          updateListPosition();
        }}
        onBlur={() => {
          revertDisplay();
        }}
        onChange={(e) => {
          editingRef.current = true;
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={(e) => {
          if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
              e.preventDefault();
              setOpen(true);
            }
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((prev) => Math.max(prev - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const opt = filtered[highlight];
            if (opt) pick(opt);
          } else if (e.key === 'Escape') {
            setOpen(false);
            revertDisplay();
          }
        }}
      />
      {open && !disabled
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              className="dem-typeahead__list dem-typeahead__list--fixed"
              style={listStyle}
              role="listbox"
            >
              {filtered.length === 0 ? (
                <li className="dem-typeahead__empty">
                  {options.length === 0
                    ? 'No reasons configured in Settings'
                    : 'No matches'}
                </li>
              ) : (
                filtered.map((opt, index) => (
                  <li key={`${opt.value}-${opt.label}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === highlight}
                      className={`dem-typeahead__option${
                        index === highlight ? ' dem-typeahead__option--active' : ''
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(opt)}
                      onMouseEnter={() => setHighlight(index)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
